import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from '../lib/api';
import {
  operatorConsole,
  type ConsoleRole,
  type ConsoleSection,
  type OperatorFlag,
  type OperatorMe,
} from '../lib/api/operatorConsole';
import { useAuth } from './AuthContext';

export interface MeContextValue {
  me: OperatorMe | null;
  role: ConsoleRole;
  permissions: ConsoleSection[];
  /** Whether this console user may open a section. The backend enforces the
   *  same rule on every request — this only decides what we bother drawing. */
  can: (section: ConsoleSection) => boolean;
  /** Whether the operator has a feature flag: does the page exist at all. */
  has: (flag: OperatorFlag) => boolean;
  isOwner: boolean;
  /** True when /me could not be read. The menu then shows only what is open
   *  to everyone, and the sidebar says so. */
  failed: boolean;
  reload: () => void;
}

const MeContext = createContext<MeContextValue | null>(null);

export function MeProvider({ children }: { children: ReactNode }) {
  const { logout } = useAuth();
  const [me, setMe] = useState<OperatorMe | null>(null);
  const [ready, setReady] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    operatorConsole
      .me()
      .then((next) => {
        if (!cancelled) setMe(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // /me is the one request that says whether this session still exists.
        // 401 (token gone, console session revoked) and 403 (removed from the
        // team, IP no longer allowed) both mean the console is unusable — end
        // the session so ProtectedRoute sends them to the login screen. Left
        // alone, the app would render a full menu over a dead session and
        // answer every click with a different error.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          void logout();
          return;
        }
        // Anything else is a blip. The console stays open, but on nothing we
        // could not read: see the fail-closed default below.
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [version, logout]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);

  const value = useMemo<MeContextValue>(() => {
    // Fail closed when /me could not be read: show only what is open to
    // everyone rather than a full menu whose every entry answers 403. Drawing
    // a section we have no right to is worse than drawing too little — the
    // backend refuses either way, but a menu that lies looks like a fault in
    // the console.
    const permissions = me?.owner.permissions ?? [];
    const role = me?.owner.role ?? 'member';
    return {
      me,
      role,
      permissions,
      isOwner: role === 'owner',
      can: (section: ConsoleSection) => permissions.includes(section),
      has: (flag: OperatorFlag) => me?.operator[flag] ?? false,
      failed: me === null,
      reload,
    };
  }, [me, reload]);

  if (!ready) {
    return (
      <div className="flex h-screen w-screen items-center justify-center text-[13px] text-[var(--ui-text-subtext,#878B95)]">
        Loading console…
      </div>
    );
  }

  return <MeContext value={value}>{children}</MeContext>;
}

export function useMe(): MeContextValue {
  const ctx = use(MeContext);
  if (!ctx) throw new Error('useMe must be used inside <MeProvider>');
  return ctx;
}
